import { select } from 'd3-selection';

import data from './data/data.json';

import './styles/graphic.scss';

// ====================================
//  Constants
// ====================================
const CONGRESS_START = 69;
const CONGRESS_END = 119;

// CNN's standard qualitative chart colors (graphics-style skill) -- these
// happen to match the Altair notebook's own color_scale exactly, since the
// notebook was built against the same palette.
const COLORS = {
  gop: '#C52622',
  democratic: '#5194C3',
  split_mixed_delegation: '#7C4EA5',
};
// Reserved for "no data" (AK/HI before statehood), kept outside the
// categorical ramp above per the style guide's missing-data rule.
const NO_DATA_COLOR = '#ececec';

const HIGHLIGHT_OPACITY = 1;
const MUTED_OPACITY = 0.35;

const DEFAULT_STATE = 'KS';
const ANNOTATION_TEXT = 'Kansas hasn\u2019t been represented by a Democratic senator in nearly 100 years.';

const MIN_CELL_WIDTH = 9;
const MAX_CELL_WIDTH = { desktop: 20, mobile: 14 };
const MOBILE_ROW_HEIGHT = 16;
// Desktop rows run a touch taller than they are wide, so 12px state names
// have room to breathe without the tiles reading as noticeably rectangular.
const DESKTOP_ROW_EXTRA_HEIGHT = 5;
const LABEL_COLUMN_WIDTH = { desktop: 108, mobile: 34 };
const HEADER_HEIGHT = 22;
// Kept in JS rather than CSS because the label column's spacer has to
// clear the title plus the tick row to keep rows aligned with their names.
const AXIS_TITLE_HEIGHT = 18;
// Congresses convene in odd years, so every 5th column is a round decade:
// 1925, 1935 ... 2025. A 4-digit label is wider than any tile ever gets,
// which rules out labeling more often than that.
const TICK_STEP = 5;

const ANNOTATION_GAP = 30;
const ANNOTATION_BOX_MAX_WIDTH = { desktop: 200, mobile: 150 };
// Vertical distance between the row and the floating annotation box, so
// the box doesn't sit directly on top of (and obscure) the row itself.
const ANNOTATION_VERTICAL_OFFSET = 45;
// How far the curved connector bows out horizontally between its two ends.
const ANNOTATION_CURVE_BOW = -30;

// ====================================
//  Derived, static data (independent of width/device, so computed once)
// ====================================
const CONGRESSES = [];
for (let n = CONGRESS_START; n <= CONGRESS_END; n += 1) CONGRESSES.push(n);

// One entry per state, sorted by the full name that's now the primary label
// (desktop rows + the dropdown both read off this order).
const STATES = (() => {
  const byCode = new Map();
  data.forEach((d) => {
    if (!byCode.has(d.state_code)) byCode.set(d.state_code, d.state_name);
  });
  return Array.from(byCode, ([code, name]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
})();

// Congress numbers are a generated range rather than a pass over the rows,
// so the start years they're labeled with come from their own lookup.
const START_YEAR_BY_CONGRESS = new Map();
data.forEach((d) => {
  START_YEAR_BY_CONGRESS.set(Number(d.congress_number), Number(d.congress_start_year));
});

const ROWS_BY_KEY = new Map();
data.forEach((d) => {
  ROWS_BY_KEY.set(`${d.state_code}_${Number(d.congress_number)}`, d);
});

// The full 50-state x 51-Congress grid, built explicitly rather than by
// iterating data.json's rows -- that keeps AK/HI's pre-statehood gaps
// (no rows at all for the 69th-85th Congresses) visible as "no data"
// cells instead of silently vanishing from the grid.
const CELLS = [];
STATES.forEach((st, rowIndex) => {
  CONGRESSES.forEach((congress, colIndex) => {
    const row = ROWS_BY_KEY.get(`${st.code}_${congress}`);
    CELLS.push({
      code: st.code,
      name: st.name,
      congress,
      rowIndex,
      colIndex,
      noData: !row,
      delegationSplit: row ? row.delegation_split : null,
      senators: row ? row.senators : null,
    });
  });
});

// ====================================
//  Small helpers
// ====================================
function ordinal(n) {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

// First calendar year of a given Congress. Falls back on the fact that the
// 1st Congress convened in 1789 and a new one begins every two years, for
// any Congress the data has no rows at all for.
function congressStartYear(n) {
  const fromData = START_YEAR_BY_CONGRESS.get(n);
  return fromData || 1789 + ((n - 1) * 2);
}

function congressYears(n) {
  const start = congressStartYear(n);
  return `${start}\u2013${start + 2}`;
}

function partyLabel(split) {
  if (split === 'gop') return 'All Republican';
  if (split === 'democratic') return 'All Democratic';
  if (split === 'split_mixed_delegation') return 'Mixed parties';
  return '';
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function cellOpacity(d, highlightedCode) {
  if (d.noData) return 1;
  return d.code === highlightedCode ? HIGHLIGHT_OPACITY : MUTED_OPACITY;
}

// Anchored on the first column rather than on round Congress numbers, so
// the series opens on 1925 and -- since the range spans exactly 50
// Congresses -- closes on 2025.
function showsTickLabel(n) {
  return (n - CONGRESS_START) % TICK_STEP === 0;
}

function cellAriaLabel(d) {
  if (d.noData) return `${d.name}: not yet a state during the ${ordinal(d.congress)} Congress`;
  return `${d.name}, ${ordinal(d.congress)} Congress (${congressYears(d.congress)}): ${partyLabel(d.delegationSplit)}`;
}

function tooltipHtml(d) {
  const head = `<strong>${d.name}</strong> \u2014 ${ordinal(d.congress)} Congress <span class="heatmap-tooltip__years">(${congressYears(d.congress)})</span>`;
  if (d.noData) {
    return `${head}<div class="heatmap-tooltip__note">Not yet a state</div>`;
  }
  return `${head}<div class="heatmap-tooltip__senators">${(d.senators || '').trim()}</div>`;
}

function positionTooltip(tooltip, event) {
  const offset = 14;
  const node = tooltip.node();
  const rect = node.getBoundingClientRect();
  const { innerWidth, innerHeight } = window;

  let left = event.pageX + offset;
  let top = event.pageY + offset;
  if (left + rect.width > innerWidth - 8) left = event.pageX - rect.width - offset;
  if (top + rect.height > innerHeight - 8) top = event.pageY - rect.height - offset;

  tooltip.style('left', `${Math.max(4, left)}px`).style('top', `${Math.max(4, top)}px`);
}

// Floats a callout box above (or, if too close to the top, below) the
// Kansas row -- offset vertically so it never sits directly on top of the
// row -- connected by a curved line ending in an open caret pointing at
// the row. Anchored off the fixed label column, rather than a spot inside
// the horizontally-scrolling grid, so it stays put and correct regardless
// of how far the grid itself is scrolled.
function renderAnnotation(root, labelsCol, isMobile) {
  const ksLabelNode = labelsCol.select('[data-state-code="KS"]').node();
  if (!ksLabelNode) return;

  const containerRect = root.node().getBoundingClientRect();
  const labelRect = ksLabelNode.getBoundingClientRect();

  // Point the curve's arrowhead lands on, right at the row.
  const anchorX = labelRect.right - containerRect.left + 25;
  const anchorY = clamp(
    (labelRect.top - containerRect.top - 10) + (labelRect.height / 2),
    20,
    containerRect.height - 20,
  );

  const boxLeft = anchorX + ANNOTATION_GAP;
  const maxWidth = ANNOTATION_BOX_MAX_WIDTH[isMobile ? 'mobile' : 'desktop'];

  // Prefer floating the box above the row; flip to below if that would
  // push it off the top of the grid (e.g. a row near the very first one).
  const preferAbove = anchorY - ANNOTATION_VERTICAL_OFFSET > 20;
  const boxAnchorY = preferAbove
    ? anchorY - ANNOTATION_VERTICAL_OFFSET
    : anchorY + ANNOTATION_VERTICAL_OFFSET;

  const box = root.append('div')
    .attr('class', 'heatmap-annotation-box')
    .style('left', `${boxLeft}px`)
    .style('top', `${boxAnchorY}px`)
    .style('max-width', `${maxWidth}px`)
    .text(ANNOTATION_TEXT);

  // Measure the box's real rendered position/size so the connector starts
  // from its actual edge (near-left corner, closest to the row) rather
  // than a guessed point.
  const boxRect = box.node().getBoundingClientRect();
  const boxRelLeft = boxRect.left - containerRect.left;
  const boxRelTop = boxRect.top - containerRect.top;
  const startX = boxRelLeft + Math.min(1, boxRect.width / 2);
  const startY = preferAbove ? boxRelTop + boxRect.height - 20 : boxRelTop;

  const svg = root.append('svg')
    .attr('class', 'heatmap-annotation-svg')
    .attr('width', containerRect.width)
    .attr('height', containerRect.height);

  svg.append('defs').append('marker')
    .attr('id', 'heatmap-annotation-arrow')
    .attr('viewBox', '0 0 10 10')
    .attr('refX', 8)
    .attr('refY', 5)
    .attr('markerWidth', 10)
    .attr('markerHeight', 10)
    .attr('orient', 'auto')
    .append('path')
    .attr('d', 'M1,-3 L8,5 L1,9')
    .attr('fill', 'none')
    .attr('stroke', '#262626')
    .attr('stroke-width', 1)
    .attr('stroke-linecap', 'round')
    .attr('stroke-linejoin', 'round');

  // Quadratic bezier so the connector bows rather than running straight,
  // with the open-caret marker landing on -- and rotating to match the
  // direction of -- the row end of the curve.
  const controlX = ((startX + anchorX) / 2) + ANNOTATION_CURVE_BOW;
  const controlY = (startY + anchorY) / 2;

  svg.append('path')
    .attr('class', 'heatmap-annotation-line')
    .attr('d', `M${startX},${startY} Q${controlX},${controlY} ${anchorX},${anchorY}`)
    .attr('marker-end', 'url(#heatmap-annotation-arrow)');
}

// ====================================
//  Module-scoped interaction state -- preserved across pym-triggered
//  redraws (index.js re-invokes this module's default export wholesale
//  on resize).
// ====================================
const state = {
  // null = default Kansas view; otherwise a state_code. Remembered while
  // showAll is on, so switching back returns to whatever was highlighted.
  selectedState: null,
  showAll: false, // every row at full opacity, nothing singled out
};

export default function (props, container, isMobile) {
  const { initialWidth } = props;

  container.html('');

  const device = isMobile ? 'mobile' : 'desktop';
  const labelColumnWidth = LABEL_COLUMN_WIDTH[device];
  const scrollAreaWidth = Math.max(120, initialWidth - labelColumnWidth);
  const cellWidth = clamp(
    Math.floor(scrollAreaWidth / CONGRESSES.length),
    MIN_CELL_WIDTH,
    MAX_CELL_WIDTH[device],
  );
  // Desktop keeps tiles near-square; mobile uses an independent, more
  // legible row height so state-code labels don't get squeezed as columns
  // shrink.
  const cellHeight = isMobile ? MOBILE_ROW_HEIGHT : cellWidth + DESKTOP_ROW_EXTRA_HEIGHT;
  const gridWidth = cellWidth * CONGRESSES.length;
  const gridHeight = cellHeight * STATES.length;

  // ------------------------------------------------------------------
  // DOM scaffold
  // ------------------------------------------------------------------
  const root = container.append('div')
    .attr('class', `heatmap${isMobile ? ' heatmap--mobile' : ''}`);

  // ---- Controls: state picker ----
  const controls = root.append('div').attr('class', 'heatmap-controls');
  const selectWrap = controls.append('div').attr('class', 'heatmap-select-wrap');
  selectWrap.append('label')
    .attr('for', 'heatmap-state-select')
    .attr('class', 'heatmap-select-label')
    .text('Highlight a state:');

  const stateSelect = selectWrap.append('select')
    .attr('id', 'heatmap-state-select')
    .attr('class', 'heatmap-select');

  stateSelect.append('option').attr('value', '').text('Select a state\u2026');
  stateSelect.selectAll('option.heatmap-state-option')
    .data(STATES)
    .enter()
    .append('option')
    .attr('class', 'heatmap-state-option')
    .attr('value', d => d.code)
    .text(d => d.name);

  stateSelect.property('value', state.selectedState || '');

  const showAllButton = controls.append('button')
    .attr('type', 'button')
    .attr('class', 'heatmap-show-all');

  // ---- Legend (direct labeling of the categorical colors) ----
  const legend = root.append('div').attr('class', 'heatmap-legend');
  const legendItems = [
    { color: COLORS.gop, label: 'All Republican' },
    { color: COLORS.democratic, label: 'All Democratic' },
    { color: COLORS.split_mixed_delegation, label: 'Mixed Parties' },
    { color: NO_DATA_COLOR, label: 'Not yet a state', outline: true },
  ];
  const legendItemSel = legend.selectAll('.heatmap-legend-item')
    .data(legendItems)
    .enter()
    .append('div')
    .attr('class', 'heatmap-legend-item');

  legendItemSel.append('span')
    .attr('class', d => `heatmap-legend-swatch${d.outline ? ' heatmap-legend-swatch--outline' : ''}`)
    .style('background-color', d => d.color);
  legendItemSel.append('span')
    .attr('class', 'heatmap-legend-label')
    .text(d => d.label);

  // ---- Body: fixed label column + horizontally scrolling grid ----
  // Structured like homecoming-heatmap's month-axis + calendar-scroll
  // split: the label column is a flex sibling of the scroller (not a
  // sticky element nested inside it), so it can't collide with the
  // grid's own scroll position.
  const bodyEl = root.append('div').attr('class', 'heatmap-body');

  const labelsCol = bodyEl.append('div')
    .attr('class', 'heatmap-labels')
    .style('width', `${labelColumnWidth}px`);

  labelsCol.append('div')
    .attr('class', 'heatmap-labels__spacer')
    .style('height', `${AXIS_TITLE_HEIGHT + HEADER_HEIGHT}px`);

  const labelsRows = labelsCol.append('div').attr('class', 'heatmap-labels__rows');

  const labelSel = labelsRows.selectAll('.heatmap-label')
    .data(STATES)
    .enter()
    .append('div')
    .attr('class', 'heatmap-label')
    .attr('data-state-code', d => d.code)
    .style('height', `${cellHeight}px`)
    .text(d => (isMobile ? d.code : d.name));

  // The title sits outside the scroller so it stays centered over the
  // visible axis instead of sliding away with the columns underneath it.
  const chart = bodyEl.append('div').attr('class', 'heatmap-chart');

  chart.append('div')
    .attr('class', 'heatmap-axis-title')
    .style('height', `${AXIS_TITLE_HEIGHT}px`)
    .text('Congress start year');

  const scroll = chart.append('div').attr('class', 'heatmap-scroll');
  const track = scroll.append('div')
    .attr('class', 'heatmap-track')
    .style('width', `${gridWidth}px`);

  const header = track.append('div')
    .attr('class', 'heatmap-header')
    .style('height', `${HEADER_HEIGHT}px`);

  header.selectAll('.heatmap-tick')
    .data(CONGRESSES)
    .enter()
    .append('div')
    .attr('class', (n) => {
      if (n === CONGRESS_START) return 'heatmap-tick heatmap-tick--first';
      if (n === CONGRESS_END) return 'heatmap-tick heatmap-tick--last';
      return 'heatmap-tick';
    })
    .style('width', `${cellWidth}px`)
    .text(n => (showsTickLabel(n) ? congressStartYear(n) : ''));

  const grid = track.append('div')
    .attr('class', 'heatmap-grid')
    .style('width', `${gridWidth}px`)
    .style('height', `${gridHeight}px`)
    .style('grid-template-columns', `repeat(${CONGRESSES.length}, ${cellWidth}px)`)
    .style('grid-template-rows', `repeat(${STATES.length}, ${cellHeight}px)`);

  const cellSel = grid.selectAll('.heatmap-cell')
    .data(CELLS)
    .enter()
    .append('div')
    .attr('class', d => `heatmap-cell${d.noData ? ' heatmap-cell--no-data' : ''}`)
    .attr('data-state-code', d => d.code)
    .style('grid-row', d => d.rowIndex + 1)
    .style('grid-column', d => d.colIndex + 1)
    .style('background-color', d => (d.noData ? NO_DATA_COLOR : COLORS[d.delegationSplit]))
    .attr('aria-label', cellAriaLabel)
    .attr('title', cellAriaLabel);

  // ------------------------------------------------------------------
  // Mobile opens parked on the present, which leaves the earlier decades
  // off the left edge. A fade and a hint line say so, and both drop away
  // once the reader has scrolled back to the start.
  // ------------------------------------------------------------------
  if (isMobile) {
    chart.append('div')
      .attr('class', 'heatmap-scroll-fade')
      .style('top', `${AXIS_TITLE_HEIGHT}px`)
      .style('height', `${HEADER_HEIGHT + gridHeight}px`);

    chart.append('p')
      .attr('class', 'heatmap-scroll-hint')
      .text('\u2039 Scroll left to see earlier years');

    const scrollEl = scroll.node();
    const syncScrollCue = () => {
      chart.classed('heatmap-chart--at-start', scrollEl.scrollLeft <= 2);
    };

    scrollEl.scrollLeft = scrollEl.scrollWidth;
    scrollEl.addEventListener('scroll', syncScrollCue);
    syncScrollCue();
  }

  // ------------------------------------------------------------------
  // Tooltip: a small mouse-following box (richer than the native title
  // attribute alone), reused across redraws rather than re-appended.
  // ------------------------------------------------------------------
  let tooltip = select(document.body).select('.heatmap-tooltip');
  if (tooltip.empty()) {
    tooltip = select(document.body).append('div').attr('class', 'heatmap-tooltip');
  }
  tooltip.style('opacity', 0);

  cellSel
    .on('mouseenter', (event, d) => {
      tooltip.style('opacity', 1).html(tooltipHtml(d));
      positionTooltip(tooltip, event);
    })
    .on('mousemove', (event) => {
      positionTooltip(tooltip, event);
    })
    .on('mouseleave', () => {
      tooltip.style('opacity', 0);
    });

  // ------------------------------------------------------------------
  // Highlight + annotation + dropdown wiring
  // ------------------------------------------------------------------
  function update() {
    const isDefaultView = !state.showAll && state.selectedState === null;
    const highlightedCode = state.showAll ? null : (state.selectedState || DEFAULT_STATE);

    cellSel.style('opacity', d => (state.showAll ? 1 : cellOpacity(d, highlightedCode)));
    labelSel.classed('heatmap-label--highlighted', d => d.code === highlightedCode);
    // While every state is lit the dropdown reads as unset, even though the
    // previous pick is still held in state ready to be restored.
    stateSelect.property('value', state.showAll ? '' : (state.selectedState || ''));
    showAllButton
      .classed('heatmap-show-all--active', state.showAll)
      .attr('aria-pressed', state.showAll ? 'true' : 'false')
      .text(state.showAll ? 'Deselect all states' : 'Select all states');

    root.selectAll('.heatmap-annotation-box, .heatmap-annotation-svg').remove();
    if (isDefaultView) {
      renderAnnotation(root, labelsCol, isMobile);
    }

    if (window.pymChild) window.pymChild.sendHeight();
  }

  stateSelect.on('change', function onChange() {
    const value = select(this).property('value');
    state.selectedState = value === '' ? null : value;
    state.showAll = false;
    update();
  });

  showAllButton.on('click', () => {
    state.showAll = !state.showAll;
    update();
  });

  update();
}
